import { Injectable, OnDestroy } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { BehaviorSubject, Observable, fromEvent, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../../environments/environment';

/**
 * Connection state for WebSocket
 */
export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'authenticated' | 'error';

/**
 * WebSocket Service
 *
 * Manages Socket.io connection to the Hub API for real-time updates.
 * Handles authentication, reconnection, and event routing.
 *
 * Events emitted by the Hub:
 * - claims.changed: User's custom claims have changed
 * - session.logout: User session invalidated
 * - invitation.received: New organization invitation
 * - ticket.updated: Support ticket updated
 * - ticket.comment: New comment on ticket
 * - ticket.assigned: Ticket assigned to agent
 * - ticket.statusChanged: Ticket status changed
 */
@Injectable({
  providedIn: 'root'
})
export class WebSocketService implements OnDestroy {
  private socket: Socket | null = null;
  private destroy$ = new Subject<void>();

  // Connection state
  private connectionStateSubject = new BehaviorSubject<ConnectionState>('disconnected');
  public connectionState$ = this.connectionStateSubject.asObservable();

  // Reconnection settings
  private maxReconnectAttempts = 5;
  private reconnectAttempts = 0;
  private reconnectDelay = 1000;

  // Heartbeat settings - keep connection alive
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private readonly HEARTBEAT_INTERVAL_MS = 30000; // 30 seconds
  private lastPongTime: number = 0;
  private readonly HEARTBEAT_TIMEOUT_MS = 60000; // Consider dead if no pong for 60s

  // Prevent concurrent connection attempts
  private isConnecting = false;

  constructor(private auth: Auth) {
    // Don't auto-connect on construction
    // Connection will be initiated when user authenticates
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopHeartbeat();
    this.disconnect();
  }

  /**
   * Connect to the WebSocket server
   * Should be called after user authentication
   */
  async connect(): Promise<void> {
    // If already connected, do nothing
    if (this.socket?.connected) {
      console.log('[WebSocket] Already connected');
      return;
    }

    // Prevent concurrent connection attempts
    if (this.isConnecting) {
      console.log('[WebSocket] Connection already in progress');
      return;
    }

    this.isConnecting = true;

    // If socket exists but not connected, clean it up first
    if (this.socket) {
      console.log('[WebSocket] Cleaning up existing socket before reconnect');
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    this.connectionStateSubject.next('connecting');

    try {
      // Get Firebase ID token for authentication
      const token = await this.getAuthToken();

      if (!token) {
        console.warn('[WebSocket] No auth token available, cannot connect');
        this.connectionStateSubject.next('disconnected');
        this.isConnecting = false;
        return;
      }

      // Create socket connection
      this.socket = io(environment.webSocketUrl, {
        transports: ['websocket'], // WebSocket only — avoids "Session ID unknown" on multi-instance deployments
        autoConnect: false,
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: this.reconnectDelay,
        reconnectionDelayMax: 5000,
        auth: {
          token
        }
      });

      // Set up event listeners
      this.setupEventListeners();

      // Connect - isConnecting will be reset in the connect/error handlers
      this.socket.connect();
    } catch (error) {
      console.error('[WebSocket] Connection error:', error);
      this.connectionStateSubject.next('error');
      this.isConnecting = false;
    }
  }

  /**
   * Disconnect from the WebSocket server
   */
  disconnect(): void {
    this.stopHeartbeat();
    this.isConnecting = false;
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    this.connectionStateSubject.next('disconnected');
    this.reconnectAttempts = 0;
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * socket.io-assigned connection id for the current session. Null when
   * disconnected. Mutations echo this back to the Hub via the
   * `X-Client-Socket-Id` header so realtime event payloads can be tagged
   * with their originating client — used by `SprayRecordRealtimeService`
   * (and any future realtime handlers) to suppress only same-environment
   * self-toasts.
   */
  getSocketId(): string | null {
    return this.socket?.id ?? null;
  }

  /**
   * Get current connection state
   */
  getConnectionState(): ConnectionState {
    return this.connectionStateSubject.value;
  }

  /**
   * Subscribe to a specific event
   */
  on<T = any>(eventName: string): Observable<T> {
    return new Observable<T>(observer => {
      if (!this.socket) {
        console.warn(`[WebSocket] Cannot subscribe to ${eventName} - not connected`);
        return;
      }

      const handler = (data: T) => {
        observer.next(data);
      };

      this.socket.on(eventName, handler);

      // Cleanup on unsubscribe
      return () => {
        this.socket?.off(eventName, handler);
      };
    }).pipe(takeUntil(this.destroy$));
  }

  /**
   * Emit an event to the server
   */
  emit(eventName: string, data?: any): void {
    if (!this.socket?.connected) {
      console.warn(`[WebSocket] Cannot emit ${eventName} - not connected`);
      return;
    }

    this.socket.emit(eventName, data);
  }

  /**
   * Authenticate the socket connection
   * Called automatically on connect, but can be called manually to re-auth
   */
  async authenticate(): Promise<boolean> {
    if (!this.socket?.connected) {
      console.warn('[WebSocket] Cannot authenticate - not connected');
      return false;
    }

    try {
      const token = await this.getAuthToken();

      if (!token) {
        console.warn('[WebSocket] No auth token available');
        return false;
      }

      return new Promise<boolean>((resolve) => {
        this.socket!.emit('authenticate', { token }, (response: { success: boolean }) => {
          if (response?.success) {
            console.log('[WebSocket] Authentication successful');
            this.connectionStateSubject.next('authenticated');
            resolve(true);
          } else {
            console.warn('[WebSocket] Authentication failed');
            this.connectionStateSubject.next('connected');
            resolve(false);
          }
        });

        // Timeout after 5 seconds
        setTimeout(() => {
          if (this.connectionStateSubject.value !== 'authenticated') {
            resolve(false);
          }
        }, 5000);
      });
    } catch (error) {
      console.error('[WebSocket] Authentication error:', error);
      return false;
    }
  }

  /**
   * Set up socket event listeners
   */
  private setupEventListeners(): void {
    if (!this.socket) return;

    // Connection events
    this.socket.on('connect', () => {
      console.log('[WebSocket] Connected to server');
      this.isConnecting = false;
      this.reconnectAttempts = 0;
      this.connectionStateSubject.next('connected');
      this.lastPongTime = Date.now();

      // Start heartbeat to keep connection alive
      this.startHeartbeat();

      // Auto-authenticate on connect
      this.authenticate();
    });

    this.socket.on('disconnect', (reason) => {
      console.log('[WebSocket] Disconnected:', reason);
      this.stopHeartbeat();
      this.isConnecting = false;
      this.connectionStateSubject.next('disconnected');

      // If server disconnected us or transport closed, try to reconnect
      if (reason === 'io server disconnect' || reason === 'transport close') {
        console.log('[WebSocket] Will attempt to reconnect...');
        setTimeout(() => this.connect(), this.reconnectDelay);
      }
    });

    this.socket.on('connect_error', (error) => {
      console.error('[WebSocket] Connection error:', error.message);
      this.isConnecting = false;
      this.reconnectAttempts++;

      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('[WebSocket] Max reconnection attempts reached');
        this.connectionStateSubject.next('error');
      }
    });

    // Ping/pong for connection health
    this.socket.on('pong', () => {
      this.lastPongTime = Date.now();
    });

    // Auth-related events
    this.socket.on('claims.changed', (data) => {
      console.log('[WebSocket] Claims changed event received');
      // This will be handled by subscribers
    });

    this.socket.on('session.logout', (data) => {
      console.log('[WebSocket] Session logout event received');
      // This will be handled by subscribers
    });
  }

  /**
   * Get Firebase auth token
   */
  private async getAuthToken(): Promise<string | null> {
    const user = this.auth.currentUser;

    if (!user) {
      return null;
    }

    try {
      return await user.getIdToken();
    } catch (error) {
      console.error('[WebSocket] Error getting auth token:', error);
      return null;
    }
  }

  /**
   * Refresh authentication (e.g., after token refresh)
   */
  async refreshAuth(): Promise<void> {
    if (this.socket?.connected) {
      await this.authenticate();
    }
  }

  /**
   * Start heartbeat interval to keep connection alive
   */
  private startHeartbeat(): void {
    this.stopHeartbeat();

    this.heartbeatInterval = setInterval(() => {
      if (!this.socket?.connected) {
        console.log('[WebSocket] Heartbeat: Socket not connected, attempting reconnect');
        this.stopHeartbeat();
        this.connect();
        return;
      }

      // Check if we've received a pong recently
      const timeSinceLastPong = Date.now() - this.lastPongTime;
      if (timeSinceLastPong > this.HEARTBEAT_TIMEOUT_MS) {
        console.warn('[WebSocket] Heartbeat: No pong received for', timeSinceLastPong, 'ms, reconnecting');
        this.socket.disconnect();
        this.connect();
        return;
      }

      // Send ping
      this.socket.emit('ping');
    }, this.HEARTBEAT_INTERVAL_MS);

    console.log('[WebSocket] Heartbeat started');
  }

  /**
   * Stop heartbeat interval
   */
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
      console.log('[WebSocket] Heartbeat stopped');
    }
  }
}
